import { useCallback, useEffect, useRef, useState } from "react";
import { InVisData, KernelType, DatasetName } from "./invis-types";
import {
  EngineState,
  createInitialState,
  resetState,
  runOptimization,
  onControlPointsChanged,
  recomputeEmbedding,
} from "./invis-engine";

export interface UseInVisEngineResult {
  data: InVisData | null;
  state: React.MutableRefObject<EngineState | null>;
  controlPoints: React.MutableRefObject<Map<number, { targetX: number; targetY: number }>>;
  loading: boolean;
  error: string | null;
  renderTick: number;
  kernel: KernelType;
  dataset: DatasetName;
  setKernel: (kernel: KernelType) => void;
  setDataset: (dataset: DatasetName) => void;
  addControlPoint: (index: number, x: number, y: number) => void;
  moveControlPoint: (index: number, x: number, y: number) => void;
  removeControlPoint: (index: number) => void;
  toggleControlPoint: (index: number) => void;
  finishDragging: () => void;
  resetAll: () => void;
}

export function useInVisEngine(): UseInVisEngineResult {
  const [data, setData] = useState<InVisData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [renderTick, setRenderTick] = useState(0);
  const [kernel, setKernelState] = useState<KernelType>("linear");
  const [dataset, setDatasetState] = useState<DatasetName>("webtender");

  const stateRef = useRef<EngineState | null>(null);
  const controlPointsRef = useRef<Map<number, { targetX: number; targetY: number }>>(new Map());
  const rafIdRef = useRef<number>(0);
  const dataRef = useRef<InVisData | null>(null);
  const needsUpdateRef = useRef(false);

  // Fetch data for the current dataset + kernel
  const loadData = useCallback((ds: DatasetName, k: KernelType) => {
    setLoading(true);
    setError(null);

    // Stop any running animation
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
    needsUpdateRef.current = false;
    controlPointsRef.current.clear();

    fetch(process.env.PUBLIC_URL + `/data/invis-${ds}-${k}.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
        return res.json();
      })
      .then((json: InVisData) => {
        dataRef.current = json;
        stateRef.current = createInitialState(json);
        setData(json);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Load initial data on mount
  useEffect(() => {
    loadData(dataset, kernel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setKernel = useCallback(
    (newKernel: KernelType) => {
      if (newKernel === kernel) return;
      setKernelState(newKernel);
      loadData(dataset, newKernel);
    },
    [kernel, dataset, loadData]
  );

  const setDataset = useCallback(
    (newDataset: DatasetName) => {
      if (newDataset === dataset) return;
      setDatasetState(newDataset);
      loadData(newDataset, kernel);
    },
    [dataset, kernel, loadData]
  );

  // Animation loop: runs full optimization on each frame while update is needed
  const startOptimizationLoop = useCallback(() => {
    needsUpdateRef.current = true;
    if (rafIdRef.current) return; // already running

    const loop = () => {
      const s = stateRef.current;
      const d = dataRef.current;
      if (!s || !d) {
        rafIdRef.current = 0;
        return;
      }

      const cp = controlPointsRef.current;
      if (cp.size === 0) {
        // No control points, recompute from current v1/v2 and stop
        recomputeEmbedding(d, s);
        setRenderTick((t) => t + 1);
        rafIdRef.current = 0;
        needsUpdateRef.current = false;
        return;
      }

      // Run the full optimization (convergence loop with time budget)
      runOptimization(d, s, cp);
      setRenderTick((t) => t + 1);

      // Keep looping as long as updates are needed (dragging continues)
      if (needsUpdateRef.current) {
        rafIdRef.current = requestAnimationFrame(loop);
      } else {
        rafIdRef.current = 0;
      }
    };

    rafIdRef.current = requestAnimationFrame(loop);
  }, []);

  const addControlPoint = useCallback(
    (index: number, x: number, y: number) => {
      controlPointsRef.current.set(index, { targetX: x, targetY: y });
      if (stateRef.current) {
        onControlPointsChanged(stateRef.current);
        startOptimizationLoop();
      }
    },
    [startOptimizationLoop]
  );

  const moveControlPoint = useCallback(
    (index: number, x: number, y: number) => {
      const cp = controlPointsRef.current.get(index);
      if (cp) {
        cp.targetX = x;
        cp.targetY = y;
        needsUpdateRef.current = true;
        startOptimizationLoop();
      }
    },
    [startOptimizationLoop]
  );

  const removeControlPoint = useCallback(
    (index: number) => {
      controlPointsRef.current.delete(index);
      if (stateRef.current) {
        if (controlPointsRef.current.size === 0) {
          // Reset to initial embedding
          resetState(stateRef.current, dataRef.current!);
          needsUpdateRef.current = false;
        } else {
          onControlPointsChanged(stateRef.current);
        }
        startOptimizationLoop();
      }
    },
    [startOptimizationLoop]
  );

  const toggleControlPoint = useCallback(
    (index: number) => {
      if (controlPointsRef.current.has(index)) {
        removeControlPoint(index);
      } else {
        const s = stateRef.current;
        if (s) {
          const x = s.embedding[index * 2];
          const y = s.embedding[index * 2 + 1];
          addControlPoint(index, x, y);
        }
      }
    },
    [addControlPoint, removeControlPoint]
  );

  const finishDragging = useCallback(() => {
    // Signal the loop to stop after the current/next frame
    needsUpdateRef.current = false;
  }, []);

  const resetAll = useCallback(() => {
    controlPointsRef.current.clear();
    needsUpdateRef.current = false;
    if (stateRef.current && dataRef.current) {
      resetState(stateRef.current, dataRef.current);
      setRenderTick((t) => t + 1);
    }
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = 0;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  return {
    data,
    state: stateRef,
    controlPoints: controlPointsRef,
    loading,
    error,
    renderTick,
    kernel,
    dataset,
    setKernel,
    setDataset,
    addControlPoint,
    moveControlPoint,
    removeControlPoint,
    toggleControlPoint,
    finishDragging,
    resetAll,
  };
}
